import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Building2,
  Cpu,
  FileDown,
  Gauge,
  Menu,
  Radio,
  Upload,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import Header from "../../Components/Header";
import AdminMap from "../../Components/Admin/Map/AdminMap";
import CreateUserForm from "../../Components/Admin/CreateUserForm/CreateUserForm";
import FirmwarePanel from "../../Components/Admin/FirmwarePanel/FirmwarePanel";
import ImportsPanel from "../../Components/Admin/ImportsPanel/ImportsPanel";
import OverviewPanel from "../../Components/Admin/OverviewPanel/OverviewPanel";
import RecognitionPanel from "../../Components/Admin/RecognitionPanel/RecognitionPanel";
import SensorsPanel from "../../Components/Admin/SensorsPanel/SensorsPanel";
import SetupCodePanel from "../../Components/Admin/SensorsPanel/SetupCodePanel";
import UnclaimedSensorsPanel from "../../Components/Admin/SensorsPanel/UnclaimedSensorsPanel";
import UsersPanel from "../../Components/Admin/UsersPanel/UsersPanel";
import VenuesPanel from "../../Components/Admin/VenuesPanel/VenuesPanel";
import { MANAGEABLE_ROLES } from "../../Services/Admin/users";
import { useAuth } from "../../Services/AuthContext";
import "../../Components/Admin/admin.css";
import "./Administrazione.css";

type SectionId =
  | "overview"
  | "venues"
  | "sensors"
  | "users"
  | "recognition"
  | "firmware"
  | "imports";

const NAVIGATION: Array<{
  id: SectionId;
  label: string;
  description: string;
  icon: typeof Gauge;
}> = [
  { id: "overview", label: "Overview", description: "Operational health and priorities", icon: Gauge },
  { id: "venues", label: "Venues", description: "Locations, plans and opening hours", icon: Building2 },
  { id: "sensors", label: "Sensors", description: "Fleet, provisioning and telemetry", icon: Cpu },
  { id: "users", label: "Users", description: "Accounts and roles", icon: Users },
  { id: "recognition", label: "Recognition", description: "Recognition health and labelling", icon: Radio },
  { id: "firmware", label: "Firmware", description: "Releases and OTA rollout", icon: Upload },
  { id: "imports", label: "Imports", description: "OSM jobs and import history", icon: FileDown },
];

/** ADMIN ser bara användarhanteringen. Resten av API:t är SUPERADMIN-only. */
const ADMIN_SECTIONS: SectionId[] = ["users"];

function VenuesScreen({ focus }: { focus: string | null }) {
  const [view, setView] = useState<"manage" | "add">(focus === "add-venue" ? "add" : "manage");

  useEffect(() => {
    if (focus === "add-venue") setView("add");
  }, [focus]);

  return (
    <section className="admin-surface" id="venue-management">
      <div className="admin-section-heading admin-section-heading--stack-mobile">
        <div>
          <span className="admin-eyebrow">Venue operations</span>
          <h2>{view === "manage" ? "Manage venues" : "Add a venue"}</h2>
        </div>
        <div className="admin-segmented" aria-label="Venue view">
          <button className={view === "manage" ? "is-active" : ""} onClick={() => setView("manage")}>All venues</button>
          <button className={view === "add" ? "is-active" : ""} onClick={() => setView("add")}><Building2 size={14} /> Add venue</button>
        </div>
      </div>
      {view === "manage" ? <VenuesPanel /> : <div id="add-venue"><AdminMap /></div>}
    </section>
  );
}

function SensorsScreen({ focus }: { focus: string | null }) {
  useEffect(() => {
    if (!focus) return;
    window.setTimeout(() => document.getElementById(focus)?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  }, [focus]);

  return (
    <div className="admin-screen-stack">
      <section className="admin-surface" id="sensor-provisioning">
        <div className="admin-section-heading">
          <div><span className="admin-eyebrow">Provisioning</span><h2>Install a new sensor</h2></div>
        </div>
        <div id="setup-code"><SetupCodePanel /></div>
      </section>
      <section className="admin-surface">
        <div className="admin-section-heading">
          <div><span className="admin-eyebrow">Awaiting assignment</span><h2>Unclaimed sensors</h2></div>
        </div>
        <UnclaimedSensorsPanel />
      </section>
      <section className="admin-surface" id="sensor-fleet">
        <div className="admin-section-heading">
          <div><span className="admin-eyebrow">Fleet management</span><h2>Connected sensors</h2></div>
        </div>
        <SensorsPanel />
      </section>
    </div>
  );
}

function UsersScreen({ focus }: { focus: string | null }) {
  const { user } = useAuth();
  const allowedRoles = (user?.role && MANAGEABLE_ROLES[user.role]) || [];
  const [creating, setCreating] = useState(focus === "create-contributor");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (focus === "create-contributor") setCreating(true);
  }, [focus]);

  return (
    <div className="admin-screen-stack">
      {creating && (
        <section className="admin-surface" id="create-contributor">
          <div className="admin-section-heading">
            <div><span className="admin-eyebrow">Account creation</span><h2>Create account</h2></div>
            <button type="button" onClick={() => setCreating(false)}>Close</button>
          </div>
          <CreateUserForm allowedRoles={allowedRoles} onCreated={() => setReloadKey((k) => k + 1)} />
        </section>
      )}
      <section className="admin-surface">
        <div className="admin-section-heading">
          <div><span className="admin-eyebrow">Account management</span><h2>All users</h2></div>
          {!creating && allowedRoles.length > 0 && <button className="admin-btn--primary" type="button" onClick={() => setCreating(true)}><UserPlus size={15} /> Create account</button>}
        </div>
        <UsersPanel reloadKey={reloadKey} />
      </section>
    </div>
  );
}

export default function Administrazione() {
  const { user } = useAuth();
  const isSuperadmin = user?.role === "SUPERADMIN";
  const navigation = isSuperadmin
    ? NAVIGATION
    : NAVIGATION.filter((item) => ADMIN_SECTIONS.includes(item.id));
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("section");
  const section: SectionId = navigation.some((item) => item.id === requested)
    ? (requested as SectionId)
    : navigation[0].id;
  const focus = searchParams.get("focus");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const current = navigation.find((item) => item.id === section)!;

  const navigate = (next: SectionId, target?: string) => {
    setSearchParams(target ? { section: next, focus: target } : { section: next });
    setMobileNavOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const content = (() => {
    switch (section) {
      case "venues": return <VenuesScreen focus={focus} />;
      case "sensors": return <SensorsScreen focus={focus} />;
      case "users": return <UsersScreen focus={focus} />;
      case "recognition": return <RecognitionPanel />;
      case "firmware": return (
        <section className="admin-surface"><div className="admin-section-heading"><div><span className="admin-eyebrow">Release management</span><h2>Firmware releases</h2></div></div><FirmwarePanel /></section>
      );
      case "imports": return <ImportsPanel />;
      default: return <OverviewPanel onNavigate={navigate} />;
    }
  })();

  return (
    <div className="admin-page dashboard-wrapper">
      <Header />
      <div className="admin-shell">
        {mobileNavOpen && <button className="admin-nav-scrim" aria-label="Close navigation" onClick={() => setMobileNavOpen(false)} />}
        <aside className={`admin-sidebar${mobileNavOpen ? " is-open" : ""}`}>
          <div className="admin-sidebar-title">
            <span>Beatspot</span>
            <strong>Operations</strong>
            <button type="button" onClick={() => setMobileNavOpen(false)} aria-label="Close navigation"><X size={18} /></button>
          </div>
          <nav aria-label="Admin sections">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <button key={item.id} type="button" className={section === item.id ? "is-active" : ""} onClick={() => navigate(item.id)}>
                  <Icon size={17} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
          <div className="admin-sidebar-foot"><span>{user?.role}</span><small>Authenticated operations console</small></div>
        </aside>

        <main className="admin-main">
          <header className="admin-page-header">
            <button className="admin-mobile-menu" type="button" onClick={() => setMobileNavOpen(true)} aria-label="Open navigation"><Menu size={19} /></button>
            <div>
              <span className="admin-breadcrumb">Operations / {current.label}</span>
              <h1>{current.label}</h1>
              <p>{current.description}</p>
            </div>
          </header>
          <div className="admin-content" key={section}>{content}</div>
        </main>
      </div>
    </div>
  );
}
